import React, { useEffect, useState } from "react";
import { TrendingUp, Send, Plus, Trash2, Loader2, Mail, Gift, History, Undo2 } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const blank = {
  name: "", blurb: "", perks: "", price: "", minScore: 40,
  priceAmount: 0, priceCurrency: "USD", priceBilling: "free", stripeCheckout: false,
  credits: 0, creditsExpireDays: 0,
  discountType: "", discountValue: 0, discountTargets: [], discountDays: 30,
  requireSignals: [], plans: [], autoSend: false, cooldownDays: 45, active: true,
};

// What the shop reads as the price. Built from the fields, so it can never disagree with what
// Stripe would actually charge.
const priceLine = (f) => {
  const amt = Number(f.priceAmount) || 0;
  if (!amt || f.priceBilling === "free") return "Free — on us";
  const money = `US$${amt.toFixed(2)}`.replace(".00", "");
  return f.priceBilling === "monthly" ? `${money} a month` : `${money} one-off`;
};

// Two taps to a sensible offer. Each one is a full package the admin can then tweak.
const TEMPLATES = [
  ["Thank-you credits", {
    name: "Thank you", blurb: "You're one of our busiest shops — here's a little extra room, on us.",
    perks: "10 extra deals\nNothing to pay", credits: 10, creditsExpireDays: 60,
    priceAmount: 0, priceBilling: "free", stripeCheckout: false,
    discountType: "", discountValue: 0, discountTargets: [], minScore: 40,
  }],
  ["Loyal shop discount", {
    name: "Loyal shop", blurb: "Your own numbers earned this: money off your next term.",
    perks: "25% off your next plan term", credits: 0, creditsExpireDays: 0,
    priceAmount: 0, priceBilling: "free", stripeCheckout: false,
    discountType: "percent", discountValue: 25, discountTargets: ["current"], discountDays: 30,
    minScore: 50,
  }],
  ["Power shop bundle", {
    name: "Power shop bundle", blurb: "More room to post and money off your next term, priced once.",
    perks: "25 extra deals\n25% off your next plan term", credits: 25, creditsExpireDays: 90,
    priceAmount: 49, priceBilling: "oneoff", stripeCheckout: true,
    discountType: "percent", discountValue: 25, discountTargets: ["current"], discountDays: 30,
    minScore: 60,
  }],
];

const rewardLine = (p, targets = []) => {
  const label = (k) => (targets.find((t) => t.key === k) || {}).label || k;
  const bits = [];
  if (p.credits > 0) bits.push(`${p.credits} credits${p.creditsExpireDays ? ` (${p.creditsExpireDays}d)` : " (never expire)"}`);
  if (p.discountType) {
    const off = p.discountType === "percent" ? `${p.discountValue}% off` : `US$${p.discountValue} off`;
    bits.push(`${off} ${(p.discountTargets || []).map(label).join(", ")} · code lasts ${p.discountDays}d`);
  }
  return bits.join(" + ");
};

// Shops whose own usage argues for more room, and the packages the admin writes for them.
export const AdminPowerUsers = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [packages, setPackages] = useState([]);
  const [signals, setSignals] = useState([]);
  const [digest, setDigest] = useState({ off: false, minScore: 40 });
  const [targets, setTargets] = useState([]);
  const [log, setLog] = useState([]);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState("");
  const [pick, setPick] = useState({});

  const load = async () => {
    const [p, u, cfg, l] = await Promise.all([
      api.get("/admin/offer-packages"), api.get("/admin/power-users"), api.get("/admin/power-digest"),
      api.get("/admin/power-users/offers"),
    ]);
    setPackages(p.data);
    setRows(u.data);
    setSignals(cfg.data.signals || []);
    setTargets(cfg.data.discountTargets || []);
    setLog(l.data || []);
    setDigest({ off: cfg.data.off, minScore: cfg.data.minScore });
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const savePackage = async () => {
    setBusy("save");
    try {
      const body = {
        ...form,
        price: "",   // the structured fields are the single source of the price line
        priceAmount: Number(form.priceAmount) || 0,
        priceCurrency: "USD",
        priceBilling: form.priceBilling || "free",
        stripeCheckout: !!form.stripeCheckout,
        perks: String(form.perks).split("\n").map((s) => s.trim()).filter(Boolean),
        minScore: Number(form.minScore) || 0,
        cooldownDays: Number(form.cooldownDays) || 45,
        credits: Number(form.credits) || 0,
        creditsExpireDays: Number(form.creditsExpireDays) || 0,
        discountType: form.discountType || null,
        discountValue: Number(form.discountValue) || 0,
        discountDays: Number(form.discountDays) || 30,
      };
      await api.post("/admin/offer-packages", body, { params: editId ? { id: editId } : {} });
      setForm(blank); setEditId(null);
      await load();
      toast({ title: "Package saved" });
    } catch (e) {
      toast({ title: "Could not save", description: e.response?.data?.detail || "Try again." });
    } finally { setBusy(""); }
  };

  const sendOffer = async (row) => {
    const pkgId = pick[row.userId] || packages[0]?.id;
    if (!pkgId) { toast({ title: "Write a package first" }); return; }
    setBusy(row.userId);
    try {
      const { data } = await api.post(`/admin/power-users/${row.userId}/offer`, null, { params: { pkg_id: pkgId } });
      toast({
        title: data.sent ? "Offer sent" : "Offer queued",
        description: data.sent ? row.email : "Held behind transactional mail until quota frees up.",
      });
      await load();
    } catch (e) {
      toast({ title: "Could not send", description: e.response?.data?.detail || "Try again." });
    } finally { setBusy(""); }
  };

  const saveDigest = async (next) => {
    setDigest(next);
    await api.post("/admin/power-digest", null, { params: next }).catch(() => {});
  };

  const toggle = (key) => setForm((f) => ({
    ...f,
    requireSignals: f.requireSignals.includes(key)
      ? f.requireSignals.filter((s) => s !== key) : [...f.requireSignals, key],
  }));

  const toggleTarget = (key) => setForm((f) => ({
    ...f,
    discountTargets: (f.discountTargets || []).includes(key)
      ? f.discountTargets.filter((s) => s !== key) : [...(f.discountTargets || []), key],
  }));

  const revoke = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/power-users/offers/${id}/revoke`);
      toast({
        title: "Offer pulled back",
        description: `${data.creditsTakenBack} unused credits removed${data.codeDisabled ? ", code switched off" : ""}.`,
      });
      await load();
    } catch (e) {
      toast({ title: "Could not pull it back", description: e.response?.data?.detail || "Try again." });
    } finally { setBusy(""); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-5" data-testid="admin-power-users">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-bold text-foreground flex items-center gap-2">
            <TrendingUp size={18} className="text-[#16a34a]" /> Power shops
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Ranked by what their own usage says they need. Offers quote their numbers back at them.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground shrink-0">
          <input type="checkbox" checked={!digest.off} data-testid="power-digest-toggle"
            onChange={(e) => saveDigest({ ...digest, off: !e.target.checked })}
            className="accent-[#16a34a] h-4 w-4" />
          Monday email
        </label>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="power-empty">No shop is near its limits yet.</p>
      ) : (
        <ul className="divide-y divide-border" data-testid="power-list">
          {rows.map((r) => (
            <li key={r.userId} className="py-3 flex flex-col sm:flex-row sm:items-start gap-3" data-testid={`power-row-${r.userId}`}>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-foreground flex flex-wrap items-center gap-2">
                  <span className="min-w-0 break-words">{r.businessName}</span>
                  <span className="text-[10px] font-extrabold uppercase tracking-wider rounded-full bg-[#e7f6ec] dark:bg-[#16341f] text-[#16a34a] px-2 py-0.5">
                    {r.score}
                  </span>
                  <span className="text-xs font-medium text-muted-foreground">{r.plan}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">{r.reasons.join(" · ")}</p>
                <p className="text-xs text-muted-foreground mt-0.5" data-testid={`power-stats-${r.userId}`}>
                  {r.stats.dealsThisMonth}/{r.stats.allowance ?? "∞"} deals this month · {r.stats.views} views · {r.stats.claims} claims · {r.stats.pins}/{r.stats.pinCap ?? "∞"} pins
                  {r.lastOfferAt ? ` · last offer: ${r.lastOfferName} (${String(r.lastOfferAt).slice(0, 10)})` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto sm:shrink-0">
                <select value={pick[r.userId] || packages[0]?.id || ""} data-testid={`power-package-${r.userId}`}
                  onChange={(e) => setPick((p) => ({ ...p, [r.userId]: e.target.value }))}
                  className="min-w-0 flex-1 sm:flex-none sm:max-w-[11rem] rounded-xl border border-border bg-background px-2.5 py-2 text-xs font-semibold text-foreground">
                  {packages.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                </select>
                <button onClick={() => sendOffer(r)} disabled={busy === r.userId || !packages.length}
                  data-testid={`power-send-${r.userId}`}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white text-xs font-bold px-3 py-2 transition-colors disabled:opacity-60">
                  {busy === r.userId ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-border pt-5 space-y-3">
        <h4 className="font-bold text-foreground text-sm flex items-center gap-2"><Mail size={15} className="text-[#16a34a]" /> Your packages</h4>
        {packages.length > 0 && (
          <ul className="space-y-2" data-testid="package-list">
            {packages.map((p) => (
              <li key={p.id} className="flex flex-col sm:flex-row sm:items-start gap-3 rounded-xl border border-border p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-foreground break-words">
                    {p.name} <span className="text-xs font-medium text-muted-foreground">· score {p.minScore}+{p.autoSend ? " · auto-sends" : ""}{p.active ? "" : " · off"}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{(p.perks || []).join(" · ")} — {p.priceLine || priceLine(p)}{p.stripeCheckout ? " · pays by card" : ""}</p>
                  {rewardLine(p, targets) && (
                    <p className="text-xs font-bold text-[#16a34a] mt-1" data-testid={`package-reward-${p.id}`}>{rewardLine(p, targets)}</p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => { setEditId(p.id); setForm({ ...p, perks: (p.perks || []).join("\n") }); }}
                    data-testid={`package-edit-${p.id}`} className="text-xs font-bold text-[#16a34a] hover:underline">Edit</button>
                  <button onClick={async () => { await api.delete(`/admin/offer-packages/${p.id}`); load(); }}
                    data-testid={`package-delete-${p.id}`} className="text-muted-foreground hover:text-rose-500"><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center gap-2" data-testid="package-templates">
          <span className="text-xs font-semibold text-muted-foreground">Start from:</span>
          {TEMPLATES.map(([label, preset]) => (
            <button key={label} type="button" data-testid={`package-template-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => { setEditId(null); setForm({ ...blank, ...preset }); }}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-bold text-muted-foreground transition-colors hover:border-[#16a34a] hover:text-[#16a34a]">
              {label}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Package name (e.g. Growth)" data-testid="package-name"
            className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" />
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">US$</span>
            <input type="number" min="0" step="1" value={form.priceAmount} data-testid="package-price-amount"
              onChange={(e) => setForm({ ...form, priceAmount: e.target.value })}
              className="w-24 rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" />
            <select value={form.priceBilling} data-testid="package-price-billing"
              onChange={(e) => setForm({ ...form, priceBilling: e.target.value, stripeCheckout: e.target.value === "free" ? false : form.stripeCheckout })}
              className="flex-1 min-w-0 rounded-xl border border-border bg-background px-2 py-2 text-sm font-semibold text-foreground">
              <option value="free">Free gift</option>
              <option value="oneoff">One-off</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-xs text-muted-foreground">
            The shop reads: <span className="font-bold text-foreground" data-testid="package-price-preview">{priceLine(form)}</span>
          </p>
          {form.priceBilling !== "free" && Number(form.priceAmount) > 0 && (
            <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
              <input type="checkbox" checked={!!form.stripeCheckout} data-testid="package-stripe-toggle"
                onChange={(e) => setForm({ ...form, stripeCheckout: e.target.checked })}
                className="accent-[#16a34a] h-4 w-4" />
              Let them pay by card in the app
            </label>
          )}
        </div>
        {form.stripeCheckout && (
          <p className="text-[11px] text-muted-foreground" data-testid="package-stripe-note">
            {form.priceBilling === "monthly"
              ? "Card checkout only handles one-off charges, so a monthly package shows the shop \"billed monthly — we'll invoice you\" instead of a Pay button. Nothing is granted until you set it up."
              : "Nothing is granted when you hit Send — the shop gets a Pay button on their dashboard, and the credits or code land the moment the payment clears."}
          </p>
        )}
        <textarea value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })}
          rows={2} placeholder="One line about why this suits them" data-testid="package-blurb"
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" />
        <textarea value={form.perks} onChange={(e) => setForm({ ...form, perks: e.target.value })}
          rows={3} placeholder={"One perk per line\n+25 deals a month\n+3 map pins"} data-testid="package-perks"
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" />

        <div className="rounded-xl border border-[#16a34a]/30 bg-[#e7f6ec] dark:bg-[#16341f] p-3 space-y-3" data-testid="package-rewards">
          <p className="text-xs font-extrabold uppercase tracking-wider text-[#16a34a] flex items-center gap-1.5">
            <Gift size={13} /> What they actually get
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
              Extra deals
              <input type="number" min="0" value={form.credits} data-testid="package-credits"
                onChange={(e) => setForm({ ...form, credits: e.target.value })}
                className="w-20 rounded-xl border border-border bg-background px-2 py-1.5 text-sm text-foreground" />
            </label>
            <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
              Expire after (days, 0 = never)
              <input type="number" min="0" value={form.creditsExpireDays} data-testid="package-credits-days"
                onChange={(e) => setForm({ ...form, creditsExpireDays: e.target.value })}
                className="w-20 rounded-xl border border-border bg-background px-2 py-1.5 text-sm text-foreground" />
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Credits land on the shop's account the moment you hit Send. Dated ones are spent first,
            and none of them are touched until the shop's monthly quota runs out.
          </p>

          <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-[#16a34a]/20">
            <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2 pt-2">
              Discount
              <select value={form.discountType} data-testid="package-discount-type"
                onChange={(e) => setForm({ ...form, discountType: e.target.value })}
                className="rounded-xl border border-border bg-background px-2 py-1.5 text-sm text-foreground">
                <option value="">None</option>
                <option value="percent">% off</option>
                <option value="amount">US$ off</option>
              </select>
            </label>
            {form.discountType && (
              <>
                <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
                  {form.discountType === "percent" ? "Percent" : "Amount"}
                  <input type="number" min="0" step="0.01" value={form.discountValue} data-testid="package-discount-value"
                    onChange={(e) => setForm({ ...form, discountValue: e.target.value })}
                    className="w-24 rounded-xl border border-border bg-background px-2 py-1.5 text-sm text-foreground" />
                </label>
                <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
                  Code valid for (days)
                  <input type="number" min="1" value={form.discountDays} data-testid="package-discount-days"
                    onChange={(e) => setForm({ ...form, discountDays: e.target.value })}
                    className="w-20 rounded-xl border border-border bg-background px-2 py-1.5 text-sm text-foreground" />
                </label>
              </>
            )}
          </div>
          {form.discountType && (
            <div className="flex flex-wrap gap-2" data-testid="package-targets">
              {targets.map((tg) => (
                <button key={tg.key} type="button" onClick={() => toggleTarget(tg.key)}
                  data-testid={`package-target-${tg.key}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${(form.discountTargets || []).includes(tg.key) ? "bg-[#16a34a] border-[#16a34a] text-white" : "border-border bg-card text-muted-foreground hover:border-[#16a34a] hover:text-[#16a34a]"}`}>
                  {tg.label}
                </button>
              ))}
            </div>
          )}
          {form.discountType && (
            <p className="text-[11px] text-muted-foreground">
              Each send mints a fresh code locked to that one shop — single use, useless if forwarded.
              100% off gives the plan away free.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2" data-testid="package-signals">
          {signals.map((s) => (
            <button key={s.key} type="button" onClick={() => toggle(s.key)} title={`weight ${s.weight}`}
              data-testid={`package-signal-${s.key}`}
              className={`rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${form.requireSignals.includes(s.key) ? "bg-[#16a34a] border-[#16a34a] text-white" : "border-border text-muted-foreground hover:border-[#16a34a] hover:text-[#16a34a]"}`}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
            Send at score
            <input type="number" value={form.minScore} onChange={(e) => setForm({ ...form, minScore: e.target.value })}
              data-testid="package-minscore" className="w-20 rounded-xl border border-border bg-background px-2 py-1.5 text-sm text-foreground" />
          </label>
          <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
            Cooldown days
            <input type="number" value={form.cooldownDays} onChange={(e) => setForm({ ...form, cooldownDays: e.target.value })}
              data-testid="package-cooldown" className="w-20 rounded-xl border border-border bg-background px-2 py-1.5 text-sm text-foreground" />
          </label>
          <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
            <input type="checkbox" checked={form.autoSend} onChange={(e) => setForm({ ...form, autoSend: e.target.checked })}
              data-testid="package-autosend" className="accent-[#16a34a] h-4 w-4" />
            Send automatically on trigger
          </label>
          <label className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="package-active" className="accent-[#16a34a] h-4 w-4" />
            Active
          </label>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={savePackage} disabled={!form.name || !!busy} data-testid="package-save"
            className="inline-flex items-center gap-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-bold px-4 py-2 transition-colors disabled:opacity-60">
            {busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {editId ? "Update package" : "Add package"}
          </button>
          {editId && (
            <button onClick={() => { setEditId(null); setForm(blank); }} data-testid="package-cancel"
              className="text-sm font-bold text-muted-foreground hover:text-foreground">Cancel</button>
          )}
        </div>
      </div>

      <div className="border-t border-border pt-5 space-y-3">
        <h4 className="font-bold text-foreground text-sm flex items-center gap-2">
          <History size={15} className="text-[#16a34a]" /> Offers sent
        </h4>
        {log.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="offer-log-empty">Nothing sent yet.</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="offer-log">
            {log.map((o) => (
              <li key={o.id} className="py-2.5 flex flex-col sm:flex-row sm:items-start gap-3" data-testid={`offer-log-${o.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-foreground break-words">
                    {o.businessName} <span className="text-xs font-medium text-muted-foreground">· {o.packageName} · {String(o.sentAt).slice(0, 10)}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5" data-testid={`offer-detail-${o.id}`}>
                    {o.payRequired && !o.paidAt ? `awaiting payment · ${o.priceLine || ""}` : (
                      o.credits > 0 ? `${o.credits} credits${o.creditsExpiresAt ? ` until ${String(o.creditsExpiresAt).slice(0, 10)}` : " (never expire)"}` : "no credits")}
                    {o.paidAt ? ` · paid ${String(o.paidAt).slice(0, 10)}` : ""}
                    {o.code ? ` · ${o.code} ${o.discountType === "percent" ? `${o.discountValue}% off` : `US$${o.discountValue} off`}` : ""}
                    {o.revokedAt ? " · PULLED BACK" : o.codeUsed ? " · code used" : o.code && !o.codeLive ? " · code expired" : ""}
                    {o.nudgedAt ? (o.nudgeSkipped ? ` · no reminder needed (${o.nudgeSkipped})` : " · reminder sent") : ""}
                    {o.held ? " · email queued" : ""}
                  </p>
                </div>
                {!o.revokedAt && !o.codeUsed && (
                  <button onClick={() => revoke(o.id)} disabled={busy === o.id} data-testid={`offer-revoke-${o.id}`}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-xs font-bold text-muted-foreground transition-colors hover:border-rose-400 hover:text-rose-500 disabled:opacity-60">
                    {busy === o.id ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />} Pull back
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminPowerUsers;
